<template>
  <div class="bg-gray-50">
    <div class="tw-container mx-auto px-4 py-8">
      <!-- Header -->
      <div class="mb-8 tw-flex items-center justify-between">
        <div>
          <h2 class="text-3xl font-bold">Your Chat Widgets</h2>
          <p class="text-gray-600">Manage AI-powered chat widgets for your ecosystem</p>
        </div>
        <ds-button
          label="Create New Widget"
          @click="createNewWidget"
          class="gap-2"
          icon="plus"
          variant="rounded"
        />
      </div>

      <!-- Search -->
      <div class="mb-6 bg-white rounded-lg shadow-sm border p-4">
        <DsInput
          icon="search"
          is-simplified
          v-model="searchQuery"
          placeholder="Search widgets by name..."
        />
      </div>

      <!-- Widget List -->
      <div class="grid gap-4">
        <div
          v-for="widget in filteredWidgets"
          :key="widget.id"
          class="bg-white rounded-lg shadow-sm border transition-all hover:shadow-lg"
        >
          <div class="p-6">
            <div class="tw-flex items-start justify-between">
              <div class="flex-1">
                <div class="mb-2 tw-flex items-center gap-3">
                  <div
                    class="w-4 h-4 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: widget.primary_color || '#21244a' }"
                  />
                  <h3 class="text-xl font-semibold">{{ widget.name }}</h3>
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getStatusBadgeClass(widget.status)"
                  >
                    {{ widget.status }}
                  </span>
                </div>
                <div class="mb-4 tw-flex gap-6 text-sm text-gray-500">
                  <span class="tw-flex items-center gap-1">
                    <Icon name="key" size="12"/>
                    {{ widget.api_key }}
                  </span>
                  <span v-if="widget.rag_focus !== 'none'" class="tw-flex items-center gap-1">
                    <Icon name="search" size="12"/>
                    Focus: {{ widget.rag_focus }}
                  </span>
                  <span>Updated {{ widget.updated_at }}</span>
                </div>
                <div class="tw-flex gap-2">
                  <ds-button
                    label="Get Embed Code"
                    variant="rounded-minimal"
                    size="sm"
                    icon="code"
                    icon-fill-color="black"
                    @click="showEmbedCode(widget)"
                  />
                  <ds-button
                    label="Configure"
                    variant="rounded"
                    size="sm"
                    @click="configureWidget(widget)"
                  />
                </div>
              </div>

              <DropdownActionsButton
                v-if="isOwner"
                :actions="getDropdownActionsForWidget(widget)"
              >
                <template v-slot:button>
                  <div class="bg-white rounded hover:bg-primary-100 hover:cursor-pointer">
                    <DsButton
                      icon="ellipsis-v"
                      icon-fill-color="black"
                      variant="rounded-minimal"
                    />
                  </div>
                </template>
              </DropdownActionsButton>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredWidgets.length === 0 && widgets.length === 0" class="text-center py-16">
        <div class="w-16 h-16 bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4">
          <Icon name="comment" class="w-8 h-8 text-gray-400"/>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">No chat widgets yet</h3>
        <p class="text-gray-500 max-w-md mx-auto mb-4">
          Create your first chat widget to embed an AI assistant on any website
        </p>
        <ds-button label="Create Your First Widget" @click="createNewWidget" icon="plus"/>
      </div>

      <!-- No Results State -->
      <div v-if="filteredWidgets.length === 0 && widgets.length > 0" class="text-center py-16">
        <div class="w-16 h-16 bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4">
          <Icon name="search" class="w-8 h-8 text-gray-400"/>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">No widgets found</h3>
        <p class="text-gray-500 max-w-md mx-auto mb-4">Try adjusting your search criteria</p>
      </div>

      <!-- Embed Code Modal -->
      <div
        v-if="embedCodeWidget"
        class="fixed inset-0 z-50 tw-flex items-center justify-center bg-black/40"
        @click.self="embedCodeWidget = null"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6">
          <div class="tw-flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold">Embed Code</h3>
            <button class="text-gray-400 hover:text-gray-600" @click="embedCodeWidget = null">
              <Icon name="cross" size="16"/>
            </button>
          </div>
          <p class="text-sm text-gray-500 mb-3">Paste this into any website to show the chat widget:</p>

          <template v-if="embedCodeWidget.mode === 'widget'">
            <div class="bg-gray-50 rounded-lg border p-4 mb-4">
              <p class="text-xs text-gray-500 font-medium mb-2">Floating widget — script tag (recommended)</p>
              <code class="text-xs text-gray-800 break-all whitespace-pre-wrap">{{ scriptEmbedCode }}</code>
            </div>
            <div class="bg-gray-50 rounded-lg border p-4 mb-4">
              <p class="text-xs text-gray-500 font-medium mb-2">Alternative — iframe</p>
              <code class="text-xs text-gray-800 break-all">{{ iframeEmbedCode }}</code>
            </div>
            <ds-button label="Copy script code" variant="rounded" @click="copyEmbedCode(scriptEmbedCode)"/>
          </template>

          <template v-else>
            <div class="bg-gray-50 rounded-lg border p-4 mb-4">
              <p class="text-xs text-gray-500 font-medium mb-2">Inline embed — iframe (recommended)</p>
              <code class="text-xs text-gray-800 break-all">{{ iframeEmbedCode }}</code>
            </div>
            <div class="bg-gray-50 rounded-lg border p-4 mb-4">
              <p class="text-xs text-gray-500 font-medium mb-2">Alternative — script tag</p>
              <code class="text-xs text-gray-800 break-all whitespace-pre-wrap">{{ scriptEmbedCode }}</code>
            </div>
            <ds-button label="Copy iframe code" variant="rounded" @click="copyEmbedCode(iframeEmbedCode)"/>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import DropdownActionsButton from '@/Domain/Shared/Components/DropdownActionsButton.vue'
  import UserMixin from '@/util/UserMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import * as ChatbotWidgetsApi from '@/Domain/ChatbotWidget/Api/chatbotWidgets'

  export default defineComponent({
    name: 'ChatbotWidgets',
    mixins: [UserMixin],
    components: { DsInput, DropdownActionsButton, Icon, DsButton },
    data () {
      return {
        widgets: [],
        loading: true,
        searchQuery: '',
        embedCodeWidget: null,
      }
    },
    computed: {
      filteredWidgets () {
        if (!this.searchQuery.trim()) return this.widgets
        const q = this.searchQuery.toLowerCase()
        return this.widgets.filter(w => w.name?.toLowerCase().includes(q))
      },
      iframeEmbedCode () {
        if (!this.embedCodeWidget) return ''
        const w = this.embedCodeWidget
        return `<iframe src="${w.embed_url}" width="100%" height="700" frameborder="0" allow="clipboard-write"></iframe>`
      },
      scriptEmbedCode () {
        if (!this.embedCodeWidget) return ''
        const w = this.embedCodeWidget
        return `<div id="ds-chat-widget" data-api-key="${w.api_key}" data-api-url="${w.ecosystem_url}"></div>\n<script src="https://static.datascouts.eu/datascouts-chat-widget.umd.js"><\/script>`
      },
    },
    async mounted () {
      await this.loadWidgets()
      this.$bus.on('chatbotWidgetDeleteConfirmation', (modalContext) => {
        this.confirmDeleteWidget(modalContext.resource)
      })
    },
    beforeUnmount () {
      this.$bus.off('chatbotWidgetDeleteConfirmation')
    },
    methods: {
      async loadWidgets () {
        try {
          this.loading = true
          const response = await ChatbotWidgetsApi.fetchChatbotWidgets()
          this.widgets = response.widgets || []
        } catch (e) {
          console.error('Failed to load chat widgets:', e)
        } finally {
          this.loading = false
        }
      },
      createNewWidget () {
        this.$router.push('/settings/chatbot-widgets/new')
      },
      configureWidget (widget) {
        this.$router.push(`/settings/chatbot-widgets/${widget.id}`)
      },
      showEmbedCode (widget) {
        this.embedCodeWidget = widget
      },
      async copyEmbedCode (code) {
        await navigator.clipboard.writeText(code)
      },
      getDropdownActionsForWidget (widget) {
        return [
          {
            onClick: () => this.openConfirmDeleteModal(widget),
            label: 'Delete',
            icon: 'trash',
            variant: 'danger',
          },
        ]
      },
      openConfirmDeleteModal (widget) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          body: 'Are you sure you want to delete this chat widget? It will stop working on any website where it is embedded.',
          resource: widget,
          modalContextType: 'chatbot-widget',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      async confirmDeleteWidget (widget) {
        if (!widget) return
        try {
          await ChatbotWidgetsApi.deleteChatbotWidget(widget.id)
          this.widgets = this.widgets.filter(w => w.id !== widget.id)
        } catch (e) {
          console.error('Failed to delete widget:', e)
        }
      },
      getStatusBadgeClass (status) {
        switch (status) {
          case 'active':   return 'bg-green-100 text-green-800'
          case 'inactive': return 'bg-gray-100 text-gray-800'
          case 'draft':    return 'bg-yellow-100 text-yellow-800'
          default:         return 'bg-gray-100 text-gray-800'
        }
      },
    },
  })
</script>

<style lang="scss" scoped>
  .tw-container { max-width: 1200px; }
</style>